export type GroupTalkTemplateId = "management" | "project" | "professional" | "custom";

export type GroupTalkMemberId =
  | "mei"
  | "cmo"
  | "cto"
  | "cfo"
  | "coo"
  | "pm"
  | "tax"
  | "law"
  | "labor"
  | "customer"
  | "consultant";

export type GroupTalkStanceId =
  | "bold"
  | "careful"
  | "idea"
  | "realist"
  | "numbers"
  | "user_first"
  | "skeptic"
  | "contrarian";

export type GroupTalkTemplateConfig = {
  id: GroupTalkTemplateId;
  title: string;
  description: string;
  members: GroupTalkMemberId[];
};

export type GroupTalkMemberConfig = {
  id: GroupTalkMemberId;
  label: string;
  roleLabel: string;
  fixed?: boolean;
};

export type GroupTalkParticipant = {
  participantId: string;
  memberId: GroupTalkMemberId;
  stanceId: GroupTalkStanceId | null;
  fixed: boolean;
};

export type GroupTalkPreviewMember = {
  participantId: string;
  memberId: GroupTalkMemberId;
  label: string;
  roleLabel: string;
  stanceId: GroupTalkStanceId | null;
  stanceLabel: string | null;
  fixed: boolean;
  imageSrc: string;
};

const GROUP_TALK_IMAGE_BASE_PATH = "/img/group-talk";
const GROUP_TALK_FALLBACK_IMAGE_PATH = "/img/no-image.png";

export const groupTalkMemberOptions: GroupTalkMemberConfig[] = [
  { id: "mei", label: "メイ", roleLabel: "ファシリテータ", fixed: true },
  { id: "cmo", label: "CMO", roleLabel: "市場分析・ブランド戦略・CAC" },
  { id: "cto", label: "CTO", roleLabel: "技術設計・開発効率・技術負債" },
  { id: "cfo", label: "CFO", roleLabel: "資金繰り・利益率・ROI" },
  { id: "coo", label: "COO", roleLabel: "運用設計・リソース配分・業務標準化" },
  { id: "pm", label: "PM", roleLabel: "体験設計・ロードマップ・優先順位" },
  { id: "tax", label: "税理士", roleLabel: "節税・決算・税務リスク" },
  { id: "law", label: "弁護士", roleLabel: "契約・法務リスク・コンプライアンス" },
  { id: "labor", label: "社労士", roleLabel: "労務手続き・助成金・雇用条件" },
  { id: "customer", label: "顧客", roleLabel: "ニーズ・不満点・支払い意欲" },
  { id: "consultant", label: "コンサル", roleLabel: "論点整理・戦略設計・意思決定支援" },
];

export const groupTalkStanceOptions: { id: GroupTalkStanceId; label: string }[] = [
  { id: "bold", label: "強気" },
  { id: "careful", label: "慎重" },
  { id: "idea", label: "発想派" },
  { id: "realist", label: "現実派" },
  { id: "numbers", label: "数字重視" },
  { id: "user_first", label: "ユーザー至上" },
  { id: "skeptic", label: "懐疑派" },
  { id: "contrarian", label: "逆張り" },
];

export const groupTalkTemplates: GroupTalkTemplateConfig[] = [
  {
    id: "management",
    title: "経営会議",
    description: "事業や経営判断を多角的に整理します。",
    members: ["mei", "cmo", "cfo", "coo"],
  },
  {
    id: "project",
    title: "プロジェクト会議",
    description: "施策や開発の進め方を実務目線で詰めます。",
    members: ["mei", "cto", "pm", "consultant"],
  },
  {
    id: "professional",
    title: "士業相談",
    description: "税務・法務・労務の論点をまとめて整理します。",
    members: ["mei", "tax", "law", "labor"],
  },
  {
    id: "custom",
    title: "自由に組む",
    description: "必要な視点だけを選んで会議を組み立てます。",
    members: ["mei"],
  },
];

export const defaultGroupTalkStances: Record<GroupTalkMemberId, GroupTalkStanceId | null> = {
  mei: null,
  cmo: "bold",
  cto: "idea",
  cfo: "numbers",
  coo: "realist",
  pm: "user_first",
  tax: "numbers",
  law: "skeptic",
  labor: "careful",
  customer: "user_first",
  consultant: "realist",
};

export function findGroupTalkMember(memberId: GroupTalkMemberId) {
  return groupTalkMemberOptions.find((option) => option.id === memberId) ?? groupTalkMemberOptions[0];
}

export function findGroupTalkStanceLabel(stanceId: GroupTalkStanceId | null) {
  if (!stanceId) return null;
  return groupTalkStanceOptions.find((option) => option.id === stanceId)?.label ?? null;
}

export function createParticipant(memberId: GroupTalkMemberId, stanceId?: GroupTalkStanceId | null): GroupTalkParticipant {
  const member = findGroupTalkMember(memberId);
  return {
    participantId: `participant_${memberId}_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
    memberId,
    stanceId: typeof stanceId === "undefined" ? defaultGroupTalkStances[memberId] : stanceId,
    fixed: Boolean(member.fixed),
  };
}

export function buildParticipantsFromTemplate(templateId: GroupTalkTemplateId): GroupTalkParticipant[] {
  const template = groupTalkTemplates.find((item) => item.id === templateId) ?? groupTalkTemplates[0];
  return template.members.map((memberId) => createParticipant(memberId));
}

export function isDuplicateParticipant(
  participants: GroupTalkParticipant[],
  memberId: GroupTalkMemberId,
  stanceId: GroupTalkStanceId | null,
  ignoreParticipantId?: string
) {
  return participants.some(
    (participant) =>
      participant.participantId !== ignoreParticipantId &&
      participant.memberId === memberId &&
      participant.stanceId === stanceId
  );
}

export function buildPreviewLabel(
  memberId: GroupTalkMemberId,
  stanceId: GroupTalkStanceId | null,
  facilitatorName: string
) {
  const member = findGroupTalkMember(memberId);
  if (memberId === "mei") return facilitatorName;
  const stanceLabel = findGroupTalkStanceLabel(stanceId);
  return stanceLabel ? `${member.label}（${stanceLabel}）` : member.label;
}

export function getGroupTalkImageSrc(memberId: GroupTalkMemberId, appearanceIndex = 1) {
  if (memberId === "mei") {
    return `${GROUP_TALK_IMAGE_BASE_PATH}/mei.png`;
  }

  const normalizedIndex = appearanceIndex % 2 === 0 ? 2 : 1;
  return `${GROUP_TALK_IMAGE_BASE_PATH}/${memberId}-${normalizedIndex}.png`;
}

export function getGroupTalkFallbackImageSrc() {
  return GROUP_TALK_FALLBACK_IMAGE_PATH;
}

export function buildGroupTalkParticipantImageMap<T extends { participantId: string; memberId: GroupTalkMemberId }>(
  participants: T[]
) {
  const appearanceCount: Partial<Record<GroupTalkMemberId, number>> = {};

  return new Map(
    participants.map((participant) => {
      const currentCount = (appearanceCount[participant.memberId] ?? 0) + 1;
      appearanceCount[participant.memberId] = currentCount;

      return [participant.participantId, getGroupTalkImageSrc(participant.memberId, currentCount)] as const;
    })
  );
}

export function buildGroupTalkPreviewMembers(
  participants: GroupTalkParticipant[],
  facilitatorName: string
): GroupTalkPreviewMember[] {
  const imageMap = buildGroupTalkParticipantImageMap(participants);

  return participants.map((participant) => {
    const member = findGroupTalkMember(participant.memberId);
    return {
      participantId: participant.participantId,
      memberId: participant.memberId,
      label: buildPreviewLabel(participant.memberId, participant.stanceId, facilitatorName),
      roleLabel: member.roleLabel,
      stanceId: participant.stanceId,
      stanceLabel: findGroupTalkStanceLabel(participant.stanceId),
      fixed: participant.fixed,
      imageSrc: imageMap.get(participant.participantId) ?? getGroupTalkFallbackImageSrc(),
    };
  });
}
